import { Button, Card, Checkbox, Group, Skeleton, Table, Text, Title } from '@mantine/core';
import { upperFirst } from '@mantine/hooks';
import { IconArrowRight } from '@tabler/icons-react';
import { i18nKeys, useTranslation } from 'locales';
import { useEffect } from 'react';
import { isDefined, isEmpty, sortBy, sumBy, times } from 'remeda';
import type { PaymentPlanResources } from 'shared/hooks/use-load-payment-plan-resources';
import { useLocalizedCurrencyFormatter } from 'shared/utils/currency';

type Step2Props = {
  isLoading: boolean;
  onClickNext: VoidFunction;
  onVisit: VoidFunction;
  resources: PaymentPlanResources | undefined;
  selectedAccountingPayments: string[];
  selectedCreditNotes: string[];
  selectedInvoices: string[];
  selectedThirdPartyPayments: string[];
  setSelectedAccountingPayments: React.Dispatch<React.SetStateAction<string[]>>;
  setSelectedCreditNotes: React.Dispatch<React.SetStateAction<string[]>>;
  setSelectedInvoices: React.Dispatch<React.SetStateAction<string[]>>;
  setSelectedPayments: React.Dispatch<React.SetStateAction<string[]>>;
};

export const Step2 = ({
  isLoading,
  onClickNext,
  onVisit,
  resources,
  selectedInvoices,
  selectedCreditNotes,
  selectedThirdPartyPayments,
  selectedAccountingPayments,
  setSelectedInvoices,
  setSelectedCreditNotes,
  setSelectedPayments,
  setSelectedAccountingPayments,
}: Step2Props) => {
  const { t } = useTranslation();
  const formatter = useLocalizedCurrencyFormatter(
    false,
    resources?.invoices[0]?.amounts.debtorCurrency.currency,
  );

  const thirdPartyPayments = resources?.thirdPartyPayments.map(payment => ({
    ...payment,
    isAccounting: false,
  }));

  const accountingPayments = resources?.accountingPayments.map(payment => ({
    ...payment,
    isAccounting: true,
  }));

  useEffect(() => onVisit(), []);

  if (isLoading || !isDefined(resources)) {
    return (
      <>
        <Title order={5} mb="xs" pt="md">
          {t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP2.INVOICES)}
        </Title>
        <Card radius="md" p={0}>
          <Table verticalSpacing="xs" style={{ borderRadius: 6 }} highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th />
                <Table.Th>{t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP2.REFERENCE)}</Table.Th>
                <Table.Th>{t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP2.DUE_DATE)}</Table.Th>
                <Table.Th>{t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP2.AMOUNT)}</Table.Th>
                <Table.Th>{t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP2.BALANCE)}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {times(10, index => (
                <Table.Tr key={index}>
                  <Table.Td />
                  <Table.Td>
                    <Skeleton h={30} w="40%" />
                  </Table.Td>
                  <Table.Td>
                    <Skeleton h={30} w="40%" />
                  </Table.Td>
                  <Table.Td>
                    <Skeleton h={30} w="30%" />
                  </Table.Td>
                  <Table.Td>
                    <Skeleton h={30} w="30%" />
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Card>
      </>
    );
  }

  const sortedInvoices = sortBy(resources.invoices, invoice => invoice.dueDate.valueOf());

  const totalSelectedBalance =
    sumSelectedRemainingBalance(resources.invoices, selectedInvoices) -
    sumSelectedRemainingBalance(resources.creditNotes, selectedCreditNotes) -
    sumSelectedRemainingBalance(resources.thirdPartyPayments, selectedThirdPartyPayments) -
    sumSelectedRemainingBalance(resources.accountingPayments, selectedAccountingPayments);

  const handleSelectAll = (resource: 'invoices' | 'creditNotes' | 'payments') => {
    switch (resource) {
      case 'invoices':
        if (selectedInvoices.length === resources.invoices.length) {
          setSelectedInvoices([]);
        } else {
          setSelectedInvoices(resources.invoices.map(invoice => invoice.id));
        }
        break;
      case 'creditNotes':
        if (selectedCreditNotes.length === resources.creditNotes.length) {
          setSelectedCreditNotes([]);
        } else {
          setSelectedCreditNotes(resources.creditNotes.map(creditNote => creditNote.id));
        }
        break;
      case 'payments': {
        const allPaymentIds = [
          ...(thirdPartyPayments ?? []).map(payment => payment.id),
          ...(accountingPayments ?? []).map(payment => payment.id),
        ];
        if (
          selectedThirdPartyPayments.length + selectedAccountingPayments.length ===
          allPaymentIds.length
        ) {
          setSelectedPayments([]);
          setSelectedAccountingPayments([]);
        } else {
          const thirdPartyIds = (thirdPartyPayments ?? []).map(payment => payment.id);
          const accountingIds = (accountingPayments ?? []).map(payment => payment.id);
          setSelectedPayments(thirdPartyIds);
          setSelectedAccountingPayments(accountingIds);
        }
        break;
      }
      default:
        break;
    }
  };

  return (
    <>
      <Title order={5} mb="xs" px="xs" pt="md">
        {upperFirst(t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP2.INVOICES))}
      </Title>
      <Card radius="md" p={0}>
        <Table verticalSpacing="xs" style={{ borderRadius: 6 }} highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ alignContent: 'center ' }}>
                <Checkbox
                  size="xs"
                  aria-label="Select all rows"
                  onChange={() => handleSelectAll('invoices')}
                  checked={selectedInvoices.length === resources.invoices.length}
                  indeterminate={
                    selectedInvoices.length > 0 &&
                    selectedInvoices.length < resources.invoices.length
                  }
                />
              </Table.Th>
              <Table.Th>{t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP2.REFERENCE)}</Table.Th>
              <Table.Th>{t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP2.DUE_DATE)}</Table.Th>
              <Table.Th>{t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP2.AMOUNT)}</Table.Th>
              <Table.Th>{t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP2.BALANCE)}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {sortedInvoices.map(invoice => (
              <Table.Tr
                key={invoice.id}
                onClick={() => {
                  if (selectedInvoices.includes(invoice.id)) {
                    setSelectedInvoices(prev => prev.filter(id => id !== invoice.id));
                  } else {
                    setSelectedInvoices(prev => [...prev, invoice.id]);
                  }
                }}
              >
                <Table.Td>
                  <Checkbox
                    aria-label="Select row"
                    checked={selectedInvoices.includes(invoice.id)}
                    onChange={() => {}}
                    size="xs"
                  />
                </Table.Td>
                <Table.Td>{invoice.reference}</Table.Td>
                <Table.Td>{invoice.dueDate.format('LL')}</Table.Td>
                <Table.Td>{formatter.format(invoice.amounts.debtorCurrency.total)}</Table.Td>
                <Table.Td>
                  {formatter.format(invoice.amounts.debtorCurrency.remainingBalance)}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Card>
      {!isEmpty(resources.creditNotes) && (
        <>
          <Title order={5} mb="xs" px="xs" pt="md">
            {upperFirst(
              t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP4.CREDIT_NOTES, {
                count: resources.creditNotes.length,
              }),
            )}
          </Title>
          <Card radius="md" p={0}>
            <Table verticalSpacing="xs" style={{ borderRadius: 6 }} highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ alignContent: 'center ' }}>
                    <Checkbox
                      size="xs"
                      aria-label="Select all rows"
                      onChange={() => handleSelectAll('creditNotes')}
                      checked={selectedCreditNotes.length === resources.creditNotes.length}
                      indeterminate={
                        selectedCreditNotes.length > 0 &&
                        selectedCreditNotes.length < resources.creditNotes.length
                      }
                    />
                  </Table.Th>
                  <Table.Th>{t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP2.REFERENCE)}</Table.Th>
                  <Table.Th>{t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP2.RECEIVED_AT)}</Table.Th>
                  <Table.Th>{t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP2.AMOUNT)}</Table.Th>
                  <Table.Th>{t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP2.BALANCE)}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {resources?.creditNotes.map(creditNote => (
                  <Table.Tr
                    key={creditNote.id}
                    onClick={() => {
                      if (selectedCreditNotes.includes(creditNote.id)) {
                        setSelectedCreditNotes(prev => prev.filter(id => id !== creditNote.id));
                      } else {
                        setSelectedCreditNotes(prev => [...prev, creditNote.id]);
                      }
                    }}
                  >
                    <Table.Td>
                      <Checkbox
                        size="xs"
                        aria-label="Select row"
                        onChange={() => {}}
                        checked={selectedCreditNotes.includes(creditNote.id)}
                      />
                    </Table.Td>
                    <Table.Td>{creditNote.reference}</Table.Td>
                    <Table.Td>{creditNote.receivedAt.format('LL')}</Table.Td>
                    <Table.Td>{formatter.format(creditNote.amounts.debtorCurrency.total)}</Table.Td>
                    <Table.Td>
                      {formatter.format(creditNote.amounts.debtorCurrency.remainingBalance)}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Card>
        </>
      )}
      {(!isEmpty(resources.thirdPartyPayments) || !isEmpty(resources.accountingPayments)) && (
        <>
          <Title order={5} mb="xs" px="xs" pt="md">
            {upperFirst(
              t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP4.PAYMENTS, {
                count: resources.thirdPartyPayments.length + resources.accountingPayments.length,
              }),
            )}
          </Title>
          <Card radius="md" p={0}>
            <Table verticalSpacing="xs" style={{ borderRadius: 6 }} highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ alignContent: 'center ' }}>
                    <Checkbox
                      size="xs"
                      aria-label="Select all rows"
                      onChange={() => handleSelectAll('payments')}
                      checked={
                        selectedThirdPartyPayments.length + selectedAccountingPayments.length ===
                        (thirdPartyPayments ?? []).length + (accountingPayments ?? []).length
                      }
                      indeterminate={
                        selectedThirdPartyPayments.length + selectedAccountingPayments.length > 0 &&
                        selectedThirdPartyPayments.length + selectedAccountingPayments.length <
                          (thirdPartyPayments ?? []).length + (accountingPayments ?? []).length
                      }
                    />
                  </Table.Th>
                  <Table.Th>{t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP2.REFERENCE)}</Table.Th>
                  <Table.Th>{t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP2.RECEIVED_AT)}</Table.Th>
                  <Table.Th>{t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP2.AMOUNT)}</Table.Th>
                  <Table.Th>{t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP2.BALANCE)}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {[...(thirdPartyPayments ?? []), ...(accountingPayments ?? [])].map(
                  ({ id, ...payment }) => {
                    const [list, update] = payment.isAccounting
                      ? [selectedAccountingPayments, setSelectedAccountingPayments]
                      : [selectedThirdPartyPayments, setSelectedPayments];
                    return (
                      <Table.Tr
                        key={id}
                        onClick={() => {
                          if (list.includes(id)) update(p => p.filter(_id => _id !== id));
                          else update(prev => [...prev, id]);
                        }}
                      >
                        <Table.Td>
                          <Checkbox
                            size="xs"
                            aria-label="Select row"
                            checked={list.includes(id)}
                            onChange={() => {}}
                          />
                        </Table.Td>
                        <Table.Td>{payment.reference}</Table.Td>
                        <Table.Td>{payment.receivedAt.format('LL')}</Table.Td>
                        <Table.Td>
                          {formatter.format(payment.amounts.debtorCurrency.total)}
                        </Table.Td>
                        <Table.Td>
                          {formatter.format(payment.amounts.debtorCurrency.remainingBalance)}
                        </Table.Td>
                      </Table.Tr>
                    );
                  },
                )}
              </Table.Tbody>
            </Table>
          </Card>
        </>
      )}
      <Group justify="space-between" mt="lg" flex={1}>
        <Group gap="xs">
          <Text>{t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP2.TOTAL_SELECTED)}</Text>
          <Text fw={600}>{formatter.format(totalSelectedBalance)}</Text>
        </Group>
        <Button
          disabled={isEmpty(selectedInvoices)}
          rightSection={<IconArrowRight stroke={1.5} size={20} />}
          onClick={onClickNext}
        >
          {t(i18nKeys.PAYMENT_PLAN.WIZARD.STEP2.NEXT)}
        </Button>
      </Group>
    </>
  );
};

type SelectableResource = {
  id: string;
  amounts: { debtorCurrency: { remainingBalance: number } };
};

const sumSelectedRemainingBalance = (resources: SelectableResource[], selectedIds: string[]) =>
  sumBy(
    resources.filter(resource => selectedIds.includes(resource.id)),
    resource => resource.amounts.debtorCurrency.remainingBalance,
  );
